/**
 * Admin Layout
 * 
 * Provides the common layout for all admin pages including:
 * - Navigation sidebar with dark glassmorphism (collapsible drawer on mobile)
 * - Sticky mobile header with hamburger toggle
 * - Protected route wrapper
 * 
 * Design: Apple-inspired dark aesthetic
 */

import React, { useState, useEffect, useRef } from 'react';
import { Navigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../../lib/auth';
import { setupGlobalErrorHandlers } from '../../lib/logging';
import { adminGlassStyles, gradients, colors } from '../../lib/design';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { useClickOutside } from '../../hooks/useClickOutside';
import {
  LayoutDashboard,
  BookOpen,
  Users,
  BarChart3,
  LogOut,
  Shield,
  Calendar,
  MessageSquare,
  Camera,
  FileText,
  Menu,
  X,
} from 'lucide-react';

interface AdminLayoutProps {
  children: React.ReactNode;
}

interface NavItem {
  path: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  exact?: boolean;
  external?: boolean;
  superAdminOnly?: boolean;
}

/**
 * Consolidated Navigation (7 items)
 * 
 * 1. Dashboard - Overview metrics
 * 2. Customers - Parents, Children, Staff, Packages, Subscriptions (merged Users + Billing)
 * 3. Content - Catalog, Published, QR Codes, Tools, Settings (merged Content + Portal + Tools + QR)
 * 4. Scheduling - Lesson scheduling
 * 5. CRM - Lead pipeline, messaging
 * 6. Analytics - Usage analytics
 * 7. System - Health monitoring, error logs, CSV export (merged Operations + Security)
 * 
 * External: Content Pipeline (scan-agent portal)
 */
const navItems: NavItem[] = [
  { path: '/admin', label: 'Dashboard', icon: LayoutDashboard, exact: true },
  { path: '/admin/customers', label: 'Customers', icon: Users },
  { path: '/admin/content', label: 'Content', icon: BookOpen },
  { path: '/admin/scheduling', label: 'Scheduling', icon: Calendar },
  { path: '/admin/crm', label: 'CRM', icon: MessageSquare },
  { path: '/admin/analytics', label: 'Analytics', icon: BarChart3 },
  { path: '/admin/system', label: 'System', icon: Shield },
  { path: '/admin/docs', label: 'Docs', icon: FileText },
  { path: '/scan-agent', label: 'Content Pipeline', icon: Camera, external: true },
];

function NavLink({ item, isActive, onClick }: { item: NavItem; isActive: boolean; onClick?: () => void }) {
  const Icon = item.icon;
  return (
    <Link
      to={item.path}
      onClick={onClick}
      className={`
        flex items-center gap-3 px-4 py-3 rounded-xl transition-all
        ${isActive
          ? 'bg-white/10 text-white font-medium'
          : 'text-white/60 hover:bg-white/5 hover:text-white'
        }
        ${item.external ? 'border-l-2 border-[#5E5CE6]/50' : ''}
      `}
      style={isActive ? { border: '1px solid rgba(255, 255, 255, 0.12)' } : undefined}
    >
      <Icon className={`w-5 h-5 ${isActive ? 'text-[#0A84FF]' : item.external ? 'text-[#5E5CE6]' : ''}`} />
      {item.label}
      {item.external && (
        <span className="ml-auto text-xs text-[#5E5CE6]/70">Portal</span>
      )}
    </Link>
  );
}

export const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const { user, profile, isAdmin, loading, signOut } = useAuth();
  const location = useLocation();
  const isMobile = useMediaQuery('(max-width: 767px)');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const drawerRef = useRef<HTMLElement>(null);

  useClickOutside(drawerRef, () => setSidebarOpen(false), sidebarOpen && isMobile);

  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    setupGlobalErrorHandlers();
  }, []);

  useEffect(() => {
    document.body.classList.add('theme-admin');
    return () => {
      document.body.classList.remove('theme-admin');
    };
  }, []);

  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: gradients.adminBg }}
      >
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-white/20 border-t-[#0A84FF]" />
      </div>
    );
  }

  if (!user || !isAdmin()) {
    return <Navigate to="/login" replace />;
  }

  const handleSignOut = async () => {
    await signOut();
  };

  const isActivePath = (item: NavItem) =>
    item.exact
      ? location.pathname === item.path
      : location.pathname === item.path || location.pathname.startsWith(item.path + '/');

  const userInitial = profile?.name?.charAt(0) || user.email?.charAt(0)?.toUpperCase() || 'A';

  const sidebarContent = (
    <>
      {/* Logo */}
      <div className="p-6 border-b border-white/8 flex items-center justify-between">
        <Link to="/admin" className="flex items-center gap-3">
          <img
            src="/turn-2.png"
            alt="Turn"
            className="h-8 w-auto object-contain brightness-0 invert"
          />
          <span className="text-sm text-white/50">Admin</span>
        </Link>
        {isMobile && (
          <button
            onClick={() => setSidebarOpen(false)}
            className="min-w-[44px] min-h-[44px] flex items-center justify-center text-white/60 hover:text-white hover:bg-white/8 rounded-xl transition-all md:hidden"
          >
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
        {navItems
          .filter((item) => !item.superAdminOnly || user?.email === 'info@turnliteracy.com')
          .map((item) => (
            <NavLink
              key={item.path}
              item={item}
              isActive={isActivePath(item)}
              onClick={isMobile ? () => setSidebarOpen(false) : undefined}
            />
          ))}
      </nav>

      {/* User Section */}
      <div className="p-4 border-t border-white/8">
        <div className="flex items-center gap-3 mb-4">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{
              background: `linear-gradient(135deg, ${colors.accent.blue}30 0%, ${colors.accent.indigo}30 100%)`,
            }}
          >
            <span className="text-[#0A84FF] font-semibold">{userInitial}</span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-white truncate">
              {profile?.name || 'Admin'}
            </p>
            <p className="text-xs text-white/50 truncate">{user.email}</p>
          </div>
        </div>
        <button
          onClick={handleSignOut}
          className="w-full flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-white/60 hover:text-[#FF453A] hover:bg-white/5 rounded-xl transition-all"
        >
          <LogOut className="w-4 h-4" />
          Sign Out
        </button>
      </div>
    </>
  );

  return (
    <div
      className="theme-admin min-h-screen flex"
      style={{ background: gradients.adminBg }}
    >
      {/* Mobile Header */}
      <header className="fixed top-0 left-0 right-0 z-30 flex items-center justify-between h-14 px-4 md:hidden" style={adminGlassStyles.sidebar}>
        <button
          onClick={() => setSidebarOpen(true)}
          className="min-w-[44px] min-h-[44px] flex items-center justify-center text-white/70 hover:text-white rounded-xl transition-all"
        >
          <Menu className="w-6 h-6" />
        </button>
        <span className="text-sm font-medium text-white/80">Admin</span>
        <div
          className="w-8 h-8 rounded-lg flex items-center justify-center"
          style={{
            background: `linear-gradient(135deg, ${colors.accent.blue}30 0%, ${colors.accent.indigo}30 100%)`,
          }}
        >
          <span className="text-xs text-[#0A84FF] font-semibold">{userInitial}</span>
        </div>
      </header>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && isMobile && (
        <div
          className="fixed inset-0 z-40 md:hidden"
          style={adminGlassStyles.overlay}
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Mobile Sidebar Drawer */}
      <aside
        ref={drawerRef}
        className={`
          fixed top-0 left-0 h-full w-72 z-50 flex flex-col transform transition-transform duration-300 ease-out
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          md:hidden
        `}
        style={adminGlassStyles.sidebar}
      >
        {sidebarContent}
      </aside>

      {/* Desktop Sidebar */}
      <aside
        className="hidden md:flex w-64 flex-col fixed h-full z-20"
        style={adminGlassStyles.sidebar}
      >
        {sidebarContent}
      </aside>

      {/* Main Content — overflow-x-hidden prevents horizontal blowout (pipeline columns), overflow-y-auto enables vertical scroll for all pages */}
      <main className="flex-1 md:ml-64 h-screen overflow-x-hidden overflow-y-auto pt-14 md:pt-0">
        {children}
      </main>
    </div>
  );
};

export default AdminLayout;
